import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container", "messages", "input", "actionMenu", "chatInterface", "contextLabel", "expandButton",
    "expandIcon", "sendButton", "sendIcon", "spinner", "actionMenuTitle", "actionMenuItems", "headerTitle",
    "aiSection", "supportSection", "supportContent", "loadingTemplate", "welcomeTemplate", "feedbackSection",
    "feedbackInput", "autoOpen", "messageInputContainer", "quickActionsPanel", "helpButton", "inputWrapper",
    "quickActionIdField"
  ]

  static values = {
    currentContext: String,
    contextData: Object,
    conversations: Object,
    currentPath: { type: String, default: 'ai' }
  }

  async connect() {
    try {
      // Store bound functions so they can be properly removed later
      this.boundHandleEscape = this.handleEscape.bind(this)
      this.boundHandleTurboStreamLoad = this.handleTurboStreamLoad.bind(this)
      this.boundHandleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)
      this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)

      // Add escape key handler
      document.addEventListener('keydown', this.boundHandleEscape)

      // Add turbo stream load handler (for scrolling to bottom of chat)
      document.addEventListener('turbo:before-stream-render', this.boundHandleTurboStreamLoad)

      // Add turbo frame load handler (for scrolling to bottom of chat)
      document.addEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)

      // Clean up localStorage when navigating away from the site
      window.addEventListener('beforeunload', this.boundHandleBeforeUnload)

      if (this.hasAutoOpenTarget) {
        localStorage.setItem('assistantOpen', 'true')
        localStorage.setItem('assistantExpanded', 'true')

        localStorage.removeItem('assistantPositionX')
        localStorage.removeItem('assistantPositionY')
      }

      this.loadState()
      this.setDraggingState()

      // Wait for targets to be available before triggering animation
      this.waitForTargetsAndTriggerAttention()

    } catch (error) {
      console.error('Error initializing assistant:', error)
    }
  }

  // Auto-resize textarea functionality
  autoResize(event) {
    const textarea = event.target
    const previousHeight = textarea.offsetHeight

    // Save current scroll position and cursor position
    const scrollTop = textarea.scrollTop
    const selectionStart = textarea.selectionStart
    const selectionEnd = textarea.selectionEnd

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Set the height to the scroll height (content height)
    textarea.style.height = textarea.scrollHeight + 'px'

    // Optional: Set max height to prevent it from getting too tall
    const maxHeight = 150 // pixels
    if (textarea.scrollHeight > maxHeight) {
      textarea.style.height = maxHeight + 'px'
      textarea.style.overflowY = 'auto'

      // Restore scroll position
      textarea.scrollTop = scrollTop

      // Ensure cursor is visible by scrolling to the cursor position
      requestAnimationFrame(() => {
        const cursorPosition = this.getCursorPosition(textarea, selectionStart)
        const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight)

        // Calculate if cursor is visible
        const cursorTop = cursorPosition.top
        const visibleTop = textarea.scrollTop
        const visibleBottom = textarea.scrollTop + textarea.clientHeight

        // If cursor is below visible area, scroll down
        if (cursorTop + lineHeight > visibleBottom) {
          textarea.scrollTop = cursorTop + lineHeight - textarea.clientHeight + 10
        }
        // If cursor is above visible area, scroll up
        else if (cursorTop < visibleTop) {
          textarea.scrollTop = cursorTop - 10
        }

        // Restore cursor position
        textarea.setSelectionRange(selectionStart, selectionEnd)
      })
    } else {
      textarea.style.overflowY = 'hidden'
    }
    
    // If height changed, scroll chat to bottom
    const newHeight = textarea.offsetHeight
    if (newHeight !== previousHeight) {
      this.scrollToBottom()
    }
  }

  // Helper method to get cursor position
  getCursorPosition(textarea, cursorIndex) {
    const div = document.createElement('div')
    const style = window.getComputedStyle(textarea)

    // Copy textarea styles to div
    div.style.position = 'absolute'
    div.style.visibility = 'hidden'
    div.style.whiteSpace = 'pre-wrap'
    div.style.wordWrap = 'break-word'
    div.style.fontSize = style.fontSize
    div.style.fontFamily = style.fontFamily
    div.style.fontWeight = style.fontWeight
    div.style.lineHeight = style.lineHeight
    div.style.letterSpacing = style.letterSpacing
    div.style.width = textarea.clientWidth + 'px'
    div.style.padding = style.padding
    div.style.border = style.border
    div.style.boxSizing = style.boxSizing

    // Get text up to cursor
    const textBeforeCursor = textarea.value.substring(0, cursorIndex)
    div.textContent = textBeforeCursor

    // Add span for cursor position
    const span = document.createElement('span')
    span.textContent = '|'
    div.appendChild(span)

    document.body.appendChild(div)

    const spanRect = span.getBoundingClientRect()
    const divRect = div.getBoundingClientRect()

    const position = {
      top: spanRect.top - divRect.top,
      left: spanRect.left - divRect.left
    }

    document.body.removeChild(div)
    return position
  }

  // Handle key press events - also disable button on Enter
  handleKeyPress(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      // Only submit if button is not disabled
      if (!this.sendButtonTarget.disabled) {
        this.submitForm(event)
      }
    }
    
    // Auto-resize after a short delay to let the content update
    setTimeout(() => {
      this.autoResize(event)
    }, 0)
  }

  // Quick Actions Panel functionality
  toggleQuickActions(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const panel = this.quickActionsPanelTarget
    const button = this.helpButtonTarget
    const isVisible = panel.classList.contains('show')
    
    if (isVisible) {
      this.hideQuickActions()
    } else {
      this.showQuickActions()
    }
  }

  showQuickActions() {
    const panel = this.quickActionsPanelTarget
    const button = this.helpButtonTarget
    const inputWrapper = this.inputWrapperTarget

    // Hide the input area
    inputWrapper.classList.add('d-none')
    
    // Show the panel
    panel.classList.remove('d-none')
    button.classList.add('active')

    // Use setTimeout to ensure DOM is ready, then add show class
    setTimeout(() => {
      panel.classList.add('show')
    }, 10)

    // Scroll to bottom to show the expanded area
    this.scrollToBottom()

    localStorage.setItem('quickActionsViewed', 'true')

    if (this.hasHelpButtonTarget) {
      this.helpButtonTarget.classList.remove('bounce-attention')
      // this.helpButtonTarget.classList.remove('glow-attention')
    }
  }

  hideQuickActions() {
    const panel = this.quickActionsPanelTarget
    const button = this.helpButtonTarget
    const inputWrapper = this.inputWrapperTarget
    
    panel.classList.remove('show')
    button.classList.remove('active')
    
    // Show the input area and hide panel after animation
    setTimeout(() => {
      panel.classList.add('d-none')
      inputWrapper.classList.remove('d-none')
    }, 300)
  }

  // Handle quick action selection
  quickOption(event) {
    const content = event.currentTarget.dataset.content;
    const quickActionId = event.currentTarget.dataset.quickActionId;
    
    // Hide quick actions first
    this.hideQuickActions();
    
    // Set the input value after input area is shown
    setTimeout(() => {
      this.inputTarget.value = content;
      
      if (this.hasQuickActionIdFieldTarget) {
        this.quickActionIdFieldTarget.value = quickActionId || '';
      }

      const form = this.inputTarget.closest('form');
      if (form) {
        this.disableSendButton();
        this.showLoadingMessage();
        form.requestSubmit();
        this.clearInput();
        this.scrollToBottom();
      }
    }, 350) // Slightly longer than hideQuickActions animation
  }

  // Scroll to bottom functionality
  scrollToBottom() {
    if (this.hasMessagesTarget) {
      const messagesContainer = this.messagesTarget

      const performScroll = () => {
        const newScrollTop = messagesContainer.scrollHeight
        messagesContainer.scrollTop = newScrollTop
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          performScroll()

          setTimeout(() => {
            performScroll()
          }, 50)
        })
      })
    }
  }

  // Clear input and reset height
  clearInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
      // Reset height after clearing
      this.inputTarget.style.height = 'auto'
      this.inputTarget.style.overflowY = 'hidden'

      if (this.hasQuickActionIdFieldTarget) {
        this.quickActionIdFieldTarget.value = '';
      }
    }
  }

  submitForm(event) {
    const form = this.inputTarget.closest('form');
    if (form) {
      this.disableSendButton();
      this.showLoadingMessage();
      form.requestSubmit();
      this.clearInput();
      this.scrollToBottom();
    }
  }

  disableSendButton() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true;
      this.sendButtonTarget.classList.add('disabled');

      // Change the icon to a spinner
      if (this.hasSendIconTarget) {
        this.sendIconTarget.classList.remove('fa-arrow-up');
        this.sendIconTarget.classList.add('fa-spinner', 'fa-spin');
      }
    }
  }

  enableSendButton() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false;
      this.sendButtonTarget.classList.remove('disabled');

      // Change the icon back to the arrow
      if (this.hasSendIconTarget) {
        this.sendIconTarget.classList.remove('fa-spinner', 'fa-spin');
        this.sendIconTarget.classList.add('fa-arrow-up');
      }
    }
  }

  showLoadingMessage() {
    if (this.hasLoadingTemplateTarget) {
      const loadingElement = this.loadingTemplateTarget.content.cloneNode(true)
      this.messagesTarget.appendChild(loadingElement)
      this.scrollToBottom()
    }
  }

  handleTurboStreamLoad(event) {
    if (event.target.target && event.target.target === 'assistant') {
      setTimeout(() => {
        this.scrollToBottom()
      }, 100)
    }

    if (event.target.target && event.target.target.startsWith('ai_chat_')) {
      // Only remove loading message if it's an assistant message
      const newMessage = event.target.templateContent.querySelector('.message')
      if (newMessage && newMessage.classList.contains('assistant')) {
        this.removeLoadingMessage()
        this.enableSendButton() // Re-enable the send button when assistant responds
      }

      // Only scroll to bottom if it's not a "load more" action
      const isLoadMore = event.target.templateContent.querySelector('[data-loading-more]')
      if (!isLoadMore) {
        setTimeout(() => {
          this.scrollToBottom()
        }, 100)
      }
    }
  }

  handleTurboFrameLoad(event) {
    document.dispatchEvent(new CustomEvent('assistant:opened', {
      bubbles: true,
      detail: { container: this.element }
    }))

    // Only scroll to bottom if it's not a "load more" action
    const isLoadMore = event.target.querySelector('[data-loading-more]')
    if (!isLoadMore) {
      setTimeout(() => {
        this.scrollToBottom()
      }, 100)
    }
  }

  removeLoadingMessage() {
    const loadingMessage = this.messagesTarget.querySelector('[data-loading-message]')
    if (loadingMessage) {
      loadingMessage.remove()
    }
  }

  setDraggingState() {
    this.isDragging = false
    this.hasDragged = false  // Add this flag
    this.currentX = 0
    this.currentY = 0
    this.initialX = 0
    this.initialY = 0
    this.xOffset = 0
    this.yOffset = 0

    // Store bound functions so they can be properly removed
    this.boundDrag = this.drag.bind(this)
    this.boundStopDragging = this.stopDragging.bind(this)
  }

  loadState() {
    if (!this.hasContainerTarget) return

    if (localStorage.getItem('assistantOpen') === 'true') {
      this.containerTarget.classList.add('open')

      if (localStorage.getItem('assistantExpanded') === 'true') {
        this.containerTarget.classList.add('expanded')
        // For some reason, the expandIconTarget doesn't exist
        // this.expandIconTarget.classList.remove('fa-up-right-and-down-left-from-center')
        // this.expandIconTarget.classList.add('fa-down-left-and-up-right-to-center')
      }

      // Restore position if saved
      const savedX = localStorage.getItem('assistantPositionX')
      const savedY = localStorage.getItem('assistantPositionY')
      if (savedX && savedY) {
        this.containerTarget.style.transform = 'none'
        this.containerTarget.style.top = `${savedY}px`
        this.containerTarget.style.left = `${savedX}px`
        this.xOffset = parseInt(savedX)
        this.yOffset = parseInt(savedY)
      }
    }
  }

  toggleAssistant() {
    this.containerTarget.classList.toggle("open")
    const isOpen = this.containerTarget.classList.contains("open")
    
    localStorage.setItem('assistantOpen', isOpen)
    
    // Dispatch custom event when assistant opens
    if (isOpen) {
      const event = new CustomEvent('assistant:opened', {
        bubbles: true,
        detail: { container: this.containerTarget }
      })
      this.element.dispatchEvent(event)

      // Scroll to bottom when assistant opens, especially if content is already loaded
      setTimeout(() => {
        this.scrollToBottom()
      }, 100)
    }
  }

  openAssistant(event) {
    if (this.containerTarget.classList.contains("open")) { return }
    this.toggleAssistant();
  }

  closeAssistant(event) {
    if (!this.containerTarget.classList.contains("open")) { return }
    this.toggleAssistant();
  }

  handleBeforeUnload = (event) => {
    // If we are doing a _top navigation, we don't want to remove state
    if (event.type === 'beforeunload' && !event.target.activeElement?.closest('a[target="_top"]')) {
      localStorage.removeItem('assistantOpen')
      localStorage.removeItem('assistantExpanded')
      localStorage.removeItem('assistantContext')
      localStorage.removeItem('assistantConversationId')
    }
  }

  disconnect() {
    // Clean up any active drag listeners
    if (this.boundDrag) {
      document.removeEventListener('mousemove', this.boundDrag)
    }
    if (this.boundStopDragging) {
      document.removeEventListener('mouseup', this.boundStopDragging)
    }

    // Remove escape key handler
    if (this.boundHandleEscape) {
      document.removeEventListener('keydown', this.boundHandleEscape)
    }

    // Remove turbo frame load handler
    if (this.boundHandleTurboFrameLoad) {
      document.removeEventListener('turbo:frame-load', this.boundHandleTurboFrameLoad)
    }

    // Remove turbo stream load handler
    if (this.boundHandleTurboStreamLoad) {
      document.removeEventListener('turbo:before-stream-render', this.boundHandleTurboStreamLoad)
    }

    // Remove beforeunload handler
    if (this.boundHandleBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundHandleBeforeUnload)
    }

    // Reset dragging state
    this.isDragging = false
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove('dragging')
    }
  }

  toggleSize(event) {
    event.preventDefault()
    if (this.containerTarget.classList.contains('expanded')) {
      this.collapse(event)
    } else {
      this.expand(event)
    }
  }

  expand(event) {
    setTimeout(() => {
      localStorage.setItem('assistantExpanded', 'true')
      this.containerTarget.classList.add('expanded')
      if (this.hasExpandIconTarget) {
        this.expandIconTarget.classList.remove('fa-up-right-and-down-left-from-center')
        this.expandIconTarget.classList.add('fa-down-left-and-up-right-to-center')
      }
    }, 100)
  }

  collapse(event) {
    setTimeout(() => {
      localStorage.removeItem('assistantExpanded')
      this.containerTarget.classList.remove('expanded')
      if (this.hasExpandIconTarget) {
        this.expandIconTarget.classList.add('fa-up-right-and-down-left-from-center')
        this.expandIconTarget.classList.remove('fa-down-left-and-up-right-to-center')
      }
    }, 100)
  }

  handleEscape = (event) => {
    if (event.key === 'Escape' && this.hasContainerTarget && this.containerTarget.classList.contains('open')) {
      this.closeAssistant(event)
    }
  }

  startDragging(event) {
    if (event.target.closest('button') || event.target.closest('input')) return

    this.isDragging = true
    this.hasDragged = false  // Reset the flag
    this.containerTarget.classList.add('dragging')

    const rect = this.containerTarget.getBoundingClientRect()
    this.initialX = event.clientX - rect.left
    this.initialY = event.clientY - rect.top


    document.addEventListener('mousemove', this.boundDrag)
    document.addEventListener('mouseup', this.boundStopDragging)
  }

  drag(event) {
    if (!this.isDragging) return

    event.preventDefault()

    this.hasDragged = true  // Set flag when actual dragging happens

    const rect = this.containerTarget.getBoundingClientRect()
    const x = event.clientX - this.initialX
    const y = event.clientY - this.initialY

    // Add 20px padding to viewport bounds
    const padding = 20
    const maxX = window.innerWidth - rect.width - padding
    const maxY = window.innerHeight - rect.height - padding

    // Keep window within viewport bounds plus padding
    this.xOffset = Math.min(Math.max(padding, x), maxX)
    this.yOffset = Math.min(Math.max(padding, y), maxY)

    // Use absolute positioning
    this.containerTarget.style.transform = 'none'
    this.containerTarget.style.top = `${this.yOffset}px`
    this.containerTarget.style.left = `${this.xOffset}px`
  }

  stopDragging() {
    this.isDragging = false
    this.containerTarget.classList.remove('dragging')

    // Only save position if there was actual dragging movement
    if (this.hasDragged) {
      localStorage.setItem('assistantPositionX', this.xOffset)
      localStorage.setItem('assistantPositionY', this.yOffset)
    }

    document.removeEventListener('mousemove', this.boundDrag)
    document.removeEventListener('mouseup', this.boundStopDragging)
  }

  setActiveButton(event) {
    let buttons = this.element.querySelectorAll('.footer-button')
    buttons.forEach(button => {
      button.classList.remove('active')
    })

    event.currentTarget.classList.add('active')
  }

  rateMessage(event) {
    const messageId = event.currentTarget.dataset.messageId;
    const sentiment = event.currentTarget.dataset.sentiment;
    const messageDiv = event.currentTarget.closest('.message');

    if (sentiment === 'negative') {
      const feedbackSection = messageDiv.querySelector('.feedback-section');
      feedbackSection.classList.remove('d-none');

      // Scroll the feedback section into view with some padding
      requestAnimationFrame(() => {
        const padding = 50;
        const feedbackRect = feedbackSection.getBoundingClientRect();
        const containerRect = this.messagesTarget.getBoundingClientRect();

        // Calculate if the feedback section is partially or fully below the visible area
        if (feedbackRect.bottom > containerRect.bottom) {
          const scrollAmount = feedbackRect.bottom - containerRect.bottom + padding;
          this.messagesTarget.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        }
      });
    } else {
      this.submitRating(messageId, sentiment);
      this.showThankYouMessage(messageDiv);
    }
  }

  cancelFeedback(event) {
    const messageDiv = event.currentTarget.closest('.message');
    const ratingDiv = messageDiv.querySelector('.message-rating');
    const feedbackSection = messageDiv.querySelector('.feedback-section');
    const ratingButtons = ratingDiv.querySelector('span, button');

    // Hide feedback form and show rating buttons
    feedbackSection.classList.add('d-none');
    const feedbackInput = messageDiv.querySelector('.feedback-section textarea');
    feedbackInput.value = '';
    ratingButtons.style.display = '';
  }

  submitFeedback(event) {
    const messageId = event.currentTarget.dataset.messageId;
    const messageDiv = event.currentTarget.closest('.message');
    const feedback = messageDiv.querySelector('.feedback-section textarea').value;

    this.submitRating(messageId, 'negative', feedback);
    this.showThankYouMessage(messageDiv);
  }

  submitRating(messageId, sentiment, feedback = null) {
    const token = document.querySelector('meta[name="csrf-token"]').content;

    fetch('/ai/ratings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({
        message_id: messageId,
        sentiment: sentiment,
        feedback: feedback
      })
    }).catch(error => {
      console.error('Error submitting rating:', error);
    });
  }

  showThankYouMessage(messageDiv) {
    const ratingDiv = messageDiv.querySelector('.message-rating');
    ratingDiv.innerHTML = '<span class="text-muted small">Thank you for your feedback.</span>';
    setTimeout(() => {
      ratingDiv.style.transition = 'opacity 0.5s ease-out';
      ratingDiv.style.opacity = '0';
      setTimeout(() => ratingDiv.remove(), 500);
    }, 1500);
  }

  getRatingButtonsHtml(messageId) {
    return `
      <span class='text-muted'>Was this helpful?</span>
      <button class="btn btn-link btn-sm p-0 ms-2"
              data-action="click->assistant#rateMessage"
              data-message-id="${messageId}"
              data-sentiment="positive">
        <i class="far fa-thumbs-up text-muted"></i>
      </button>
      <button class="btn btn-link btn-sm p-0 ms-1"
              data-action="click->assistant#rateMessage"
              data-message-id="${messageId}"
              data-sentiment="negative">
        <i class="far fa-thumbs-down text-muted"></i>
      </button>
    `;
  }

  waitForTargetsAndTriggerAttention() {
    const checkTarget = () => {
      if (this.hasHelpButtonTarget) {
        this.triggerQuickActionsAttention()
      } else {
        setTimeout(checkTarget, 100)
      }
    }

    setTimeout(checkTarget, 200)
  }

  triggerQuickActionsAttention() {
    // Only show attention animation if user hasn't used quick actions before
    // if (!localStorage.getItem('quickActionsViewed')) {
      setTimeout(() => {
        if (this.hasHelpButtonTarget) {
          this.helpButtonTarget.classList.add('bounce-attention')
          // this.helpButtonTarget.classList.add('glow-attention')

          // Remove animation after it completes
          setTimeout(() => {
            if (this.hasHelpButtonTarget) {
              this.helpButtonTarget.classList.remove('bounce-attention')
              // this.helpButtonTarget.classList.remove('glow-attention')
            }
          }, 2000)
        }
      }, 500)
    // }
  }
}
